import { Box, formatSchoolYear, Grid, Stack, useElementBreakpoints } from "@compass/core";
import { Select } from "components";
import {
  LkStudentTabId,
  Permission,
  PermissionAction,
  StudentTranscript as StudentTranscriptModel,
  StudentTranscriptYear,
  StudentViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { Absences } from "./Absences";
import { Gpas } from "./Gpas";
import { Grades } from "./Grades";
import { Tests } from "./Tests";
import { isAuthorized } from "services";

export type FeTranscriptListFilter = {
  schoolYear?: number | undefined;
};

export const StudentTranscript = (props: {
  initialLoad: boolean;
  studentId: number;
  activeSchoolYear: number | undefined;
  onGetList: () => void;
  viewResource: StudentViewResource;
}) => {
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [transcript, setTranscript] = useState<StudentTranscriptModel>({});
  const [listFilter, setListFilter] = useState<FeTranscriptListFilter>({});

  const studentTabIds = props.viewResource.studentTabs?.map((x) => x.id);
  const selectedTranscriptYear = transcript.years?.find((x) => x.year === listFilter.schoolYear);

  const fetch = () => {
    ApiService.students.getTranscript(props.studentId).then((response) => {
      setTranscript(response);
    });
  };

  useEffect(() => {
    if (props.initialLoad) {
      fetch();
    }
  }, [props.initialLoad]);

  useEffect(() => {
    if (props.activeSchoolYear) {
      setListFilter({ schoolYear: props.activeSchoolYear });
    }
  }, [props.activeSchoolYear]);

  return (
    <Stack spacing="md" height="100%" overflow="auto">
      <Box ref={breakpointsRef} pt={1}>
        <Grid container spacing="sm" alignItems="center">
          <Grid item xs={setBreakpoints({ md: 2, xs: 5 })}>
            <Select
              label="School Year"
              required
              value={transcript.years?.find((x) => x.year === listFilter?.schoolYear)}
              options={transcript.years}
              getOptionLabel={(option) => formatSchoolYear(option.year)!}
              onChange={(e, value) =>
                setListFilter(
                  produce((draft) => {
                    draft.schoolYear = value ? value.year : undefined;
                  })
                )
              }
              size="small"
            />
          </Grid>
        </Grid>
      </Box>

      <Box flexGrow={1} overflow={"auto"}>
        <Grid container spacing="md" height="100%" overflow={"auto"}>
          {isAuthorized(Permission.StudentGrade, PermissionAction.View) &&
            studentTabIds?.includes(LkStudentTabId.Grade) && (
              <Grid item xs={setBreakpoints({ md: 7, xs: 12 })}>
                <Grades
                  studentId={props.studentId}
                  year={selectedTranscriptYear?.year}
                  gradeTerms={selectedTranscriptYear?.gradeTerms || []}
                  onGetList={() => {
                    fetch();
                    props.onGetList();
                  }}
                />
              </Grid>
            )}

          <Grid item xs={setBreakpoints({ md: 5, xs: 12 })}>
            <Stack spacing="md">
              {isAuthorized(Permission.StudentGpa, PermissionAction.View) &&
                studentTabIds?.includes(LkStudentTabId.Gpa) && (
                  <Gpas
                    studentId={props.studentId}
                    year={selectedTranscriptYear?.year}
                    gpas={selectedTranscriptYear?.gpas || []}
                    onGetList={() => {
                      fetch();
                      props.onGetList();
                    }}
                  />
                )}

              {isAuthorized(Permission.StudentTest, PermissionAction.View) &&
                studentTabIds?.includes(LkStudentTabId.Test) && (
                  <Tests
                    studentId={props.studentId}
                    year={selectedTranscriptYear?.year}
                    tests={selectedTranscriptYear?.tests || []}
                    onGetList={() => {
                      fetch();
                      props.onGetList();
                    }}
                  />
                )}

              {isAuthorized(Permission.StudentAbsence, PermissionAction.View) &&
                studentTabIds?.includes(LkStudentTabId.Absence) && (
                  <Absences
                    studentId={props.studentId}
                    year={selectedTranscriptYear?.year}
                    absences={selectedTranscriptYear?.absences || []}
                    onGetList={() => {
                      fetch();
                      props.onGetList();
                    }}
                  />
                )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
